import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowRight,
  LuChevronDown,
  LuChevronRight,
  LuCircleEllipsis,
  LuFileText,
  LuHeadphones,
  LuMessageCircle,
  LuMinus,
  LuPlus,
  LuSearch,
  LuShoppingCart,
  LuThumbsDown,
  LuThumbsUp,
  LuTruck,
  LuUser,
  LuWallet,
} from "react-icons/lu";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/faq.scss";

const CATEGORIES = [
  { id: "buying", title: "Buying", count: 12, icon: LuShoppingCart, tone: "blue" },
  { id: "selling", title: "Selling", count: 15, icon: LuUser, tone: "green" },
  { id: "payments", title: "Payments", count: 10, icon: LuWallet, tone: "orange" },
  { id: "orders", title: "Orders & Delivery", count: 14, icon: LuTruck, tone: "purple" },
  { id: "account", title: "Account & Settings", count: 9, icon: LuHeadphones, tone: "cyan" },
  { id: "other", title: "Other Topics", count: 8, icon: LuCircleEllipsis, tone: "gray" },
];

const FAQ_ITEMS = [
  {
    id: "create-account",
    category: "account",
    question: "How do I create an account on Trocanet?",
    answer:
      "To create an account, click on the Sign Up button on the homepage. Fill in your business details, verify your email and mobile number, and complete your profile. Once verified, you can start buying or selling on the platform.",
    tags: ["account", "signup", "register"],
  },
  {
    id: "post-rfq",
    category: "buying",
    question: "How can I post an RFQ?",
    answer:
      "Go to the RFQ section from your dashboard or the homepage, click Create RFQ, enter product details, quantity, delivery location, and timeline. Submit the request and suppliers will respond with quotations.",
    tags: ["rfq", "buying", "quotation"],
  },
  {
    id: "contact-supplier",
    category: "buying",
    question: "How do I contact a supplier?",
    answer:
      "Open a supplier profile or product listing and use the Contact Supplier or Send Enquiry option. You can also message suppliers directly from RFQ responses and order conversations.",
    tags: ["supplier", "contact", "enquiry"],
  },
  {
    id: "payment-methods",
    category: "payments",
    question: "What payment methods are supported?",
    answer:
      "Trocanet supports bank transfer, UPI, credit/debit cards, and approved B2B payment options depending on your account type and transaction module. Payment availability may vary by order type.",
    tags: ["payment", "upi", "bank"],
  },
  {
    id: "track-order",
    category: "orders",
    question: "How can I track my order?",
    answer:
      "Go to My Orders in your dashboard, open the order details page, and view shipment status, dispatch updates, and delivery milestones in real time when logistics tracking is enabled.",
    tags: ["order", "track", "delivery"],
  },
  {
    id: "cancel-rfq",
    category: "buying",
    question: "How do I cancel an RFQ?",
    answer:
      "Open the RFQ from your dashboard, choose the active request, and select Cancel RFQ if it has not been awarded or converted into an order. Cancelled RFQs will no longer accept new quotations.",
    tags: ["rfq", "cancel"],
  },
  {
    id: "upload-products",
    category: "selling",
    question: "How do I upload products?",
    answer:
      "From the seller dashboard, open Product Catalog, click Add Product, upload images, enter specifications, pricing, and stock details, then publish the listing after review.",
    tags: ["product", "upload", "catalog"],
  },
  {
    id: "verify-supplier",
    category: "selling",
    question: "How do I verify my supplier account?",
    answer:
      "Complete business KYC, upload company registration documents, GST details, and authorized contact information. Our verification team will review your submission and update your account status.",
    tags: ["seller", "verification", "kyc"],
  },
  {
    id: "wrong-order",
    category: "orders",
    question: "What to do if I receive the wrong order?",
    answer:
      "Document the issue with photos, raise a dispute from Help Center, and include your order reference number. Our support team will coordinate with the supplier for replacement, refund, or resolution.",
    tags: ["order", "dispute", "wrong"],
  },
  {
    id: "update-profile",
    category: "account",
    question: "How do I update my profile information?",
    answer:
      "Go to Account Settings, open Profile or Company Details, update the required fields, and save changes. Some business details may require re-verification before they become active.",
    tags: ["profile", "settings", "account"],
  },
];

const POPULAR_ARTICLES = [
  { title: "How to Create an Account", to: "/help-center/topics/account" },
  { title: "How to Post an RFQ", to: "/help-center/topics/buyer" },
  { title: "How to Track Your Order", to: "/help-center/articles?q=orders" },
  { title: "Payment Methods & Charges", to: "/help-center/topics/orders-payments" },
  { title: "Seller Verification Process", to: "/help-center/articles?q=verification" },
];

const INITIAL_VISIBLE = 6;

export default function FaqPage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [openId, setOpenId] = useState("create-account");
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const filteredFaqs = useMemo(() => {
    const query = search.trim().toLowerCase();
    return FAQ_ITEMS.filter((item) => {
      const matchesCategory = !activeCategory || item.category === activeCategory;
      if (!query) return matchesCategory;
      const haystack = [item.question, item.answer, ...item.tags].join(" ").toLowerCase();
      return matchesCategory && haystack.includes(query);
    });
  }, [activeCategory, search]);

  const visibleFaqs = filteredFaqs.slice(0, visibleCount);
  const canLoadMore = visibleCount < filteredFaqs.length;

  const toggleQuestion = (id) => {
    setOpenId((current) => (current === id ? "" : id));
  };

  const onHelpful = (helpful) => {
    toast.success(helpful ? "Thanks for your feedback!" : "Thanks! We'll work on improving this page.");
  };

  return (
    <div className="faq-page">
      <B2BHubHomeHeader />

      <main className="faq-main">
        <nav className="faq-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>&gt;</span>
          <Link to="/help-center">Help Center</Link>
          <span aria-hidden>&gt;</span>
          <span aria-current="page">FAQ</span>
        </nav>

        <header className="faq-hero">
          <h1>Frequently Asked Questions</h1>
          <p>Find answers to the most common questions about using Trocanet.</p>
          <label className="faq-search">
            <LuSearch aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setVisibleCount(INITIAL_VISIBLE);
              }}
              placeholder="Search for questions, topics or keywords..."
            />
          </label>
        </header>

        <section className="faq-categories">
          <h2>Browse by Category</h2>
          <div className="faq-categories__grid">
            {CATEGORIES.map((item) => {
              const Icon = item.icon;
              const selected = activeCategory === item.id;
              return (
                <button
                  key={item.id}
                  type="button"
                  className={`faq-category${selected ? " is-selected" : ""}`}
                  onClick={() => {
                    setActiveCategory((current) => (current === item.id ? "" : item.id));
                    setVisibleCount(INITIAL_VISIBLE);
                  }}
                >
                  <span className={`faq-category__icon faq-tone--${item.tone}`} aria-hidden>
                    <Icon />
                  </span>
                  <strong>{item.title}</strong>
                  <small>{item.count} Articles</small>
                </button>
              );
            })}
          </div>
        </section>

        <div className="faq-layout">
          <section className="faq-questions">
            <h2>Top Questions</h2>
            {visibleFaqs.length === 0 ? (
              <div className="faq-empty">No questions found. Try a different search or category.</div>
            ) : (
              <div className="faq-accordion">
                {visibleFaqs.map((item) => {
                  const open = openId === item.id;
                  return (
                    <article key={item.id} className={`faq-item${open ? " is-open" : ""}`}>
                      <button
                        type="button"
                        className="faq-item__trigger"
                        onClick={() => toggleQuestion(item.id)}
                        aria-expanded={open}
                      >
                        <span>{item.question}</span>
                        {open ? <LuMinus aria-hidden /> : <LuPlus aria-hidden />}
                      </button>
                      {open ? <div className="faq-item__answer">{item.answer}</div> : null}
                    </article>
                  );
                })}
              </div>
            )}

            {canLoadMore ? (
              <button
                type="button"
                className="faq-load-more"
                onClick={() => setVisibleCount((count) => count + 4)}
              >
                Load More Questions <LuChevronDown aria-hidden />
              </button>
            ) : null}
          </section>

          <aside className="faq-aside">
            <section className="faq-side-card faq-side-card--help">
              <span className="faq-side-card__icon" aria-hidden>
                <LuMessageCircle />
              </span>
              <h2>Still Need Help?</h2>
              <p>Can&apos;t find the answer you&apos;re looking for? Our support team is here to help.</p>
              <Link to="/contact?enquiry=general">
                Contact Support <LuArrowRight aria-hidden />
              </Link>
            </section>

            <section className="faq-side-card">
              <h2>Popular Articles</h2>
              <div className="faq-articles">
                {POPULAR_ARTICLES.map((item) => (
                  <Link key={item.title} to={item.to} className="faq-article-link">
                    <span className="faq-article-link__icon" aria-hidden>
                      <LuFileText />
                    </span>
                    <span>{item.title}</span>
                    <LuChevronRight aria-hidden />
                  </Link>
                ))}
              </div>
              <Link to="/help-center/articles" className="faq-view-all">
                View All Articles <LuArrowRight aria-hidden />
              </Link>
            </section>

            <section className="faq-side-card faq-side-card--feedback">
              <h2>Was this page helpful?</h2>
              <p>Your feedback helps us improve.</p>
              <div className="faq-feedback">
                <button type="button" className="faq-feedback__btn faq-feedback__btn--yes" onClick={() => onHelpful(true)}>
                  <LuThumbsUp aria-hidden /> Yes
                </button>
                <button type="button" className="faq-feedback__btn faq-feedback__btn--no" onClick={() => onHelpful(false)}>
                  <LuThumbsDown aria-hidden /> No
                </button>
              </div>
            </section>
          </aside>
        </div>
      </main>

      <HubHomeFooter />
    </div>
  );
}
